import { useState } from 'react';
import { BookOpen, FileText, HelpCircle, Tag, Loader2 } from 'lucide-react';
import { FOREST_GREEN, MID_GRAY, BORDER, SHADOWS, WHITE } from '@/styles/tokens';
import type { ProposedKnowledgeBase } from '@/types/plan';

interface KnowledgePreviewCardProps {
  kb: ProposedKnowledgeBase;
  onConfirm: () => void;
  onGoBack: () => void;
  isCreating?: boolean;
}

export function KnowledgePreviewCard({
  kb,
  onConfirm,
  onGoBack,
  isCreating,
}: KnowledgePreviewCardProps) {
  const [showGaps, setShowGaps] = useState(false);
  const gaps = kb.gaps || [];
  const articles = kb.articles || [];
  const tags = kb.tags || [];

  return (
    <div
      style={{
        border: `1px solid ${BORDER.DEFAULT}`,
        borderRadius: 8,
        background: WHITE,
        boxShadow: SHADOWS.sm,
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: '12px 16px',
          background: `${FOREST_GREEN.DEFAULT}08`,
          borderBottom: `1px solid ${BORDER.DEFAULT}`,
          display: 'flex',
          alignItems: 'center',
          gap: 8,
        }}
      >
        <BookOpen style={{ width: 16, height: 16, color: FOREST_GREEN.DEFAULT }} />
        <span style={{ fontSize: 13, fontWeight: 600, color: FOREST_GREEN.DEFAULT }}>
          Knowledge Base Preview
        </span>
        {kb.completeness_estimate != null && (
          <span style={{ marginLeft: 'auto', fontSize: 11, color: MID_GRAY.DEFAULT }}>
            {kb.completeness_estimate}% complete
          </span>
        )}
      </div>

      {/* Body */}
      <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 12, maxHeight: 260, overflowY: 'auto' }}>
        {/* Title & Description */}
        <div>
          <div style={{ fontSize: 15, fontWeight: 600, color: '#1F2937' }}>{kb.title}</div>
          <div style={{ fontSize: 13, color: MID_GRAY.DEFAULT, marginTop: 4 }}>{kb.description}</div>
        </div>

        {/* Domain */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 11, fontWeight: 600, color: MID_GRAY.DEFAULT, textTransform: 'uppercase' }}>Domain</span>
          <span style={{ fontSize: 12, color: '#1F2937', background: '#F3F4F6', padding: '2px 8px', borderRadius: 4 }}>{kb.domain}</span>
        </div>

        {/* Tags */}
        {tags.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {tags.map((tag) => (
              <span
                key={tag}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 3,
                  fontSize: 11, padding: '2px 8px', borderRadius: 10,
                  background: `${FOREST_GREEN.DEFAULT}10`, color: FOREST_GREEN.DEFAULT,
                  border: `1px solid ${FOREST_GREEN.DEFAULT}20`,
                }}
              >
                <Tag style={{ width: 10, height: 10 }} />
                {tag}
              </span>
            ))}
          </div>
        )}

        {/* Articles */}
        {articles.length > 0 && (
          <div>
            <div style={{ fontSize: 11, fontWeight: 600, color: MID_GRAY.DEFAULT, textTransform: 'uppercase', marginBottom: 6 }}>
              Articles ({articles.length})
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {articles.map((article, i) => (
                <div
                  key={i}
                  style={{
                    display: 'flex', alignItems: 'flex-start', gap: 6,
                    padding: '6px 8px', borderRadius: 6, background: '#F9FAFB',
                    border: `1px solid ${BORDER.DEFAULT}`,
                  }}
                >
                  <FileText style={{ width: 12, height: 12, color: MID_GRAY.DEFAULT, marginTop: 2, flexShrink: 0 }} />
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 500, color: '#1F2937' }}>{article.title}</div>
                    {article.outline && (
                      <div style={{ fontSize: 11, color: MID_GRAY.DEFAULT, marginTop: 2 }}>{article.outline}</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Gaps */}
        {gaps.length > 0 && (
          <div>
            <button
              onClick={() => setShowGaps(!showGaps)}
              style={{
                fontSize: 11, fontWeight: 600, color: MID_GRAY.DEFAULT, textTransform: 'uppercase',
                background: 'none', border: 'none', cursor: 'pointer', padding: 0,
                display: 'flex', alignItems: 'center', gap: 4,
              }}
            >
              <HelpCircle style={{ width: 12, height: 12 }} />
              Gaps to fill later ({gaps.length})
            </button>
            {showGaps && (
              <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 3 }}>
                {gaps.map((gap, i) => (
                  <div key={i} style={{ fontSize: 11, color: MID_GRAY.DEFAULT, padding: '4px 8px', background: '#FEF3C7', borderRadius: 4, border: '1px solid #FDE68A' }}>
                    <strong>{gap.topic}</strong>: {gap.suggested_question}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Actions */}
      <div
        style={{
          padding: '12px 16px',
          borderTop: `1px solid ${BORDER.DEFAULT}`,
          display: 'flex', gap: 8, justifyContent: 'flex-end',
        }}
      >
        <button
          onClick={onGoBack}
          disabled={isCreating}
          style={{
            padding: '6px 14px', borderRadius: 6, fontSize: 12, fontWeight: 500,
            border: `1px solid ${BORDER.DEFAULT}`, background: WHITE, color: MID_GRAY.DEFAULT,
            cursor: isCreating ? 'not-allowed' : 'pointer', opacity: isCreating ? 0.5 : 1,
          }}
        >
          Go Back
        </button>
        <button
          onClick={onConfirm}
          disabled={isCreating}
          style={{
            padding: '6px 14px', borderRadius: 6, fontSize: 12, fontWeight: 500,
            border: 'none', background: FOREST_GREEN.DEFAULT, color: WHITE,
            cursor: isCreating ? 'not-allowed' : 'pointer', opacity: isCreating ? 0.9 : 1,
            display: 'flex', alignItems: 'center', gap: 6,
          }}
        >
          {isCreating && <Loader2 style={{ width: 14, height: 14, animation: 'spin 1s linear infinite' }} />}
          {isCreating ? 'Creating...' : 'Create Knowledge Base'}
        </button>
      </div>
    </div>
  );
}
